export type SoundEffectType = 'correct' | 'wrong' | 'victory' | 'click' | 'tick';

export interface SoundLogEntry {
  type: SoundEffectType;
  timestamp: number;
  key?: string;
}

export function numberToWords(num: number): string {
  if (num <= 0) return String(num);
  const ones = [
    '', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
    'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
    'seventeen', 'eighteen', 'nineteen',
  ];
  const tens = [
    '', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety',
  ];

  if (num < 20) return ones[num];
  if (num < 100) {
    const rem = num % 10;
    return rem === 0 ? tens[Math.floor(num / 10)] : `${tens[Math.floor(num / 10)]}-${ones[rem]}`;
  }
  if (num < 200) {
    const rem = num % 100;
    if (rem === 0) return 'one hundred';
    return `one hundred ${numberToWords(rem)}`;
  }
  return String(num);
}

export function formatTextForNaturalNarration(text: string): string {
  if (!text || typeof text !== 'string') return '';

  let formatted = text;

  // 1. Replace underscore blanks: any sequence of one or more underscores becomes simply "blank"
  formatted = formatted.replace(/_+/g, ' blank ');

  // 2. Canonical book names with ordinal prefixes (e.g. "1 John" -> "First John", "2 Samuel" -> "Second Samuel")
  formatted = formatted.replace(/\b1\s+([A-Z][a-z]+)/g, 'First $1');
  formatted = formatted.replace(/\b2\s+([A-Z][a-z]+)/g, 'Second $1');
  formatted = formatted.replace(/\b3\s+([A-Z][a-z]+)/g, 'Third $1');

  // 3. Format Bible Chapter:Verse ranges, e.g. "John 3:1-16", "Genesis 1:1", "Psalm 23:1-6"
  // Handles ranges: Book Chapter:VerseStart-VerseEnd
  // Example: "John 3:1-16" -> "John, chapter three, verses one to sixteen"
  const BIBLE_REF_RANGE_REGEX = /\b([A-Za-z]+(?:\s+[A-Za-z]+)?)\s+(\d+)[:\.](\d+)[-–—](\d+)\b/g;
  formatted = formatted.replace(
    BIBLE_REF_RANGE_REGEX,
    (_match, book, chap, vStart, vEnd) => {
      const c = numberToWords(parseInt(chap, 10));
      const s = numberToWords(parseInt(vStart, 10));
      const e = numberToWords(parseInt(vEnd, 10));
      return `${book}, chapter ${c}, verses ${s} to ${e}`;
    }
  );

  // Handles single verses: Book Chapter:Verse
  // Example: "John 3:16" -> "John, chapter three, verse sixteen"
  const BIBLE_REF_SINGLE_REGEX = /\b([A-Za-z]+(?:\s+[A-Za-z]+)?)\s+(\d+)[:\.](\d+)\b/g;
  formatted = formatted.replace(
    BIBLE_REF_SINGLE_REGEX,
    (_match, book, chap, v) => {
      const c = numberToWords(parseInt(chap, 10));
      const verseWord = numberToWords(parseInt(v, 10));
      return `${book}, chapter ${c}, verse ${verseWord}`;
    }
  );

  // Clean up punctuation and spacing for human cadence
  formatted = formatted.replace(/\s+/g, ' ').trim();

  return formatted;
}

class SoundAndSpeechEngine {
  private audioCtx: AudioContext | null = null;
  private bgOscillators: OscillatorNode[] = [];
  private bgGain: GainNode | null = null;
  private isBgPlaying = false;
  private activeSfxNodes: Array<{ osc: OscillatorNode; gain: GainNode }> = [];
  private playedDedupeKeys = new Set<string>();
  private lastPrimarySfxAt = 0;
  private lastPrimarySfxType: SoundEffectType | null = null;

  public sfxEnabled = true;
  public bgMusicEnabled = false;
  public bgMusicVolume = 0.35;
  public narrationEnabled = false;
  public narrateInstructions = false;
  public narrateChoices = false;
  public narrationVoice = 'Kore';
  public narrationSpeed = 1.0;
  private currentAudio: HTMLAudioElement | null = null;

  public lastPlayedSound: SoundEffectType | null = null;
  public playCounts: Record<SoundEffectType, number> = {
    correct: 0,
    wrong: 0,
    victory: 0,
    click: 0,
    tick: 0,
  };
  public soundHistory: SoundLogEntry[] = [];

  private getContext(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!this.audioCtx) {
      const AudioCtx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      if (AudioCtx) {
        this.audioCtx = new AudioCtx();
      }
    }
    if (this.audioCtx && this.audioCtx.state === 'suspended') {
      this.audioCtx.resume().catch(() => {});
    }
    return this.audioCtx;
  }

  public stopActiveSfx() {
    for (const node of this.activeSfxNodes) {
      try {
        node.osc.stop();
      } catch {
        // Already stopped
      }
      try {
        node.osc.disconnect();
        node.gain.disconnect();
      } catch {
        // Ignore disconnect errors
      }
    }
    this.activeSfxNodes = [];
  }

  public resetSessionSounds() {
    this.stopActiveSfx();
    this.playedDedupeKeys.clear();
  }

  private recordSoundEvent(type: SoundEffectType, key?: string) {
    const now = Date.now();
    this.lastPlayedSound = type;
    this.playCounts[type] = (this.playCounts[type] || 0) + 1;
    this.soundHistory.push({ type, timestamp: now, key });
    if (this.soundHistory.length > 100) {
      this.soundHistory.shift();
    }
    if (type === 'correct' || type === 'wrong' || type === 'victory') {
      this.lastPrimarySfxAt = now;
      this.lastPrimarySfxType = type;
    }
  }

  public playClick() {
    if (!this.sfxEnabled) return;
    // Do not overlap with an active answer or victory sound
    if (Date.now() - this.lastPrimarySfxAt < 350) return;
    this.stopActiveSfx();
    this.recordSoundEvent('click');

    const ctx = this.getContext();
    if (!ctx) return;
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(520, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(680, ctx.currentTime + 0.045);
      gain.gain.setValueAtTime(0.08, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.045);
      osc.connect(gain);
      gain.connect(ctx.destination);
      this.activeSfxNodes.push({ osc, gain });
      osc.start();
      osc.stop(ctx.currentTime + 0.05);
    } catch {
      // Ignore audio context restrictions
    }
  }

  public playCorrect(dedupeKey?: string) {
    if (!this.sfxEnabled) return;
    if (dedupeKey) {
      if (this.playedDedupeKeys.has(dedupeKey)) return;
      this.playedDedupeKeys.add(dedupeKey);
    } else if (
      this.lastPrimarySfxType === 'correct' &&
      Date.now() - this.lastPrimarySfxAt < 180
    ) {
      return;
    }

    // Stop any previous sound effect immediately so sounds never overlap
    this.stopActiveSfx();
    this.recordSoundEvent('correct', dedupeKey);

    const ctx = this.getContext();
    if (!ctx) return;
    try {
      const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
      notes.forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        const start = ctx.currentTime + idx * 0.065;
        osc.frequency.setValueAtTime(freq, start);
        gain.gain.setValueAtTime(0.14, start);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.22);
        osc.connect(gain);
        gain.connect(ctx.destination);
        this.activeSfxNodes.push({ osc, gain });
        osc.start(start);
        osc.stop(start + 0.23);
      });
    } catch {
      // Ignore
    }
  }

  public playWrong(dedupeKey?: string) {
    if (!this.sfxEnabled) return;
    if (dedupeKey) {
      if (this.playedDedupeKeys.has(dedupeKey)) return;
      this.playedDedupeKeys.add(dedupeKey);
    } else if (
      this.lastPrimarySfxType === 'wrong' &&
      Date.now() - this.lastPrimarySfxAt < 180
    ) {
      return;
    }

    // Stop any previous sound effect immediately so sounds never overlap
    this.stopActiveSfx();
    this.recordSoundEvent('wrong', dedupeKey);

    const ctx = this.getContext();
    if (!ctx) return;
    try {
      const notes = [311.13, 261.63, 220.0]; // D#4, C4, A3 descending buzzer
      notes.forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sawtooth';
        const start = ctx.currentTime + idx * 0.1;
        osc.frequency.setValueAtTime(freq, start);
        gain.gain.setValueAtTime(0.09, start);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.2);
        osc.connect(gain);
        gain.connect(ctx.destination);
        this.activeSfxNodes.push({ osc, gain });
        osc.start(start);
        osc.stop(start + 0.21);
      });
    } catch {
      // Ignore
    }
  }

  public playTick(urgent = false) {
    if (!this.sfxEnabled) return;
    // Do not overlap with an active answer or victory sound
    if (Date.now() - this.lastPrimarySfxAt < 400) return;
    this.recordSoundEvent('tick');

    const ctx = this.getContext();
    if (!ctx) return;
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(urgent ? 880 : 440, ctx.currentTime);
      gain.gain.setValueAtTime(urgent ? 0.09 : 0.04, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.04);
      osc.connect(gain);
      gain.connect(ctx.destination);
      this.activeSfxNodes.push({ osc, gain });
      osc.start();
      osc.stop(ctx.currentTime + 0.045);
    } catch {
      // Ignore
    }
  }

  public playVictoryFanfare(dedupeKey?: string) {
    if (!this.sfxEnabled) return;
    if (dedupeKey) {
      if (this.playedDedupeKeys.has(dedupeKey)) return;
      this.playedDedupeKeys.add(dedupeKey);
    } else if (
      this.lastPrimarySfxType === 'victory' &&
      Date.now() - this.lastPrimarySfxAt < 500
    ) {
      return;
    }

    // Stop any previous answer or click sound immediately so the win fanfare never overlaps
    this.stopActiveSfx();
    this.recordSoundEvent('victory', dedupeKey);

    const ctx = this.getContext();
    if (!ctx) return;
    try {
      // Distinct celebratory fanfare melody (C5 -> E5 -> G5 -> C6 -> G5 -> C6 -> E6)
      const fanfareNotes = [
        { freq: 523.25, delay: 0.0, duration: 0.14 },
        { freq: 659.25, delay: 0.11, duration: 0.14 },
        { freq: 783.99, delay: 0.22, duration: 0.14 },
        { freq: 1046.5, delay: 0.33, duration: 0.22 },
        { freq: 783.99, delay: 0.52, duration: 0.14 },
        { freq: 1046.5, delay: 0.65, duration: 0.24 },
        { freq: 1318.5, delay: 0.82, duration: 0.55 },
      ];
      fanfareNotes.forEach(({ freq, delay, duration }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        const start = ctx.currentTime + delay;
        osc.frequency.setValueAtTime(freq, start);
        gain.gain.setValueAtTime(0.16, start);
        gain.gain.exponentialRampToValueAtTime(0.001, start + duration);
        osc.connect(gain);
        gain.connect(ctx.destination);
        this.activeSfxNodes.push({ osc, gain });
        osc.start(start);
        osc.stop(start + duration + 0.02);
      });
    } catch {
      // Ignore
    }
  }

  public toggleBackgroundMusic(enable: boolean, volume = 0.35) {
    this.bgMusicEnabled = enable;
    this.bgMusicVolume = volume;
    if (!enable) {
      this.stopBackgroundMusic();
      return;
    }
    if (this.isBgPlaying) {
      if (this.bgGain && this.audioCtx) {
        this.bgGain.gain.setValueAtTime(
          volume * 0.06,
          this.audioCtx.currentTime
        );
      }
      return;
    }
    const ctx = this.getContext();
    if (!ctx) return;
    try {
      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(volume * 0.05, ctx.currentTime);
      masterGain.connect(ctx.destination);
      this.bgGain = masterGain;

      const freqs = [261.63, 329.63, 392.0]; // Warm C major ambient pad
      this.bgOscillators = freqs.map((f) => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(f, ctx.currentTime);
        osc.connect(masterGain);
        osc.start();
        return osc;
      });
      this.isBgPlaying = true;
    } catch {
      // Ignore
    }
  }

  public stopBackgroundMusic() {
    this.bgOscillators.forEach((osc) => {
      try {
        osc.stop();
        osc.disconnect();
      } catch {
        // Ignore
      }
    });
    this.bgOscillators = [];
    this.isBgPlaying = false;
  }

  public stopNarration() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }

  private ttsClientCache = new Map<string, string>();
  private ttsClientCooldownUntil = 0;

  public async narrateQuizQuestion(params: {
    questionText: string;
    instructionText?: string;
    options?: string[];
    language: string;
    force?: boolean;
  }): Promise<void> {
    if (!this.narrationEnabled && !params.force) return;
    const cleanQ = formatTextForNaturalNarration(params.questionText.trim());
    const segments: string[] = [cleanQ];
    if (this.narrateInstructions && params.instructionText?.trim()) {
      segments.push(formatTextForNaturalNarration(params.instructionText.trim()));
    }
    if (
      this.narrateChoices &&
      Array.isArray(params.options) &&
      params.options.length > 0
    ) {
      segments.push(params.options.map(o => formatTextForNaturalNarration(o)).join(', '));
    }
    const fullSpeech = segments.filter(Boolean).join('. ');
    await this.speakText(fullSpeech, {
      language: params.language,
      force: params.force,
    });
  }

  public async speakText(
    text: string,
    options?: { language?: string; force?: boolean }
  ): Promise<void> {
    if (!this.narrationEnabled && !options?.force) return;
    this.stopNarration();

    const naturalCleanText = formatTextForNaturalNarration(text.trim());
    if (!naturalCleanText) return;

    const lang = options?.language || 'en';
    const cacheKey = `${this.narrationVoice}:${lang}:${naturalCleanText}`;
    const cachedBase64 = this.ttsClientCache.get(cacheKey);
    if (cachedBase64) {
      try {
        const audio = new Audio(`data:audio/wav;base64,${cachedBase64}`);
        audio.playbackRate = Number(this.narrationSpeed) || 1.0;
        this.currentAudio = audio;
        await audio.play();
        return;
      } catch {
        // Fall through to Web Speech API
      }
    }

    if (options?.force && Date.now() >= this.ttsClientCooldownUntil) {
      try {
        const res = await fetch('/api/tts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: naturalCleanText,
            voiceName: this.narrationVoice || 'Kore',
            language: lang,
          }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.audioBase64) {
            this.ttsClientCache.set(cacheKey, data.audioBase64);
            const audio = new Audio(
              `data:audio/wav;base64,${data.audioBase64}`
            );
            audio.playbackRate = Number(this.narrationSpeed) || 1.0;
            this.currentAudio = audio;
            await audio.play();
            return;
          } else {
            this.ttsClientCooldownUntil = Date.now() + 30000;
          }
        }
      } catch {
        this.ttsClientCooldownUntil = Date.now() + 30000;
      }
    }

    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      try {
        const utter = new SpeechSynthesisUtterance(naturalCleanText);
        utter.rate = Math.max(0.85, (Number(this.narrationSpeed) || 1.0) * 0.95);
        utter.pitch = 1.0;
        const voices = window.speechSynthesis.getVoices();
        let targetVoice = null;

        if (lang === 'ko') {
          targetVoice = voices.find(
            (v) =>
              v.lang.toLowerCase().startsWith('ko') ||
              v.lang.toLowerCase().includes('kr')
          );
          utter.lang = targetVoice?.lang || 'ko-KR';
        } else if (lang === 'zh-CN') {
          targetVoice =
            voices.find(
              (v) =>
                v.lang === 'zh-CN' ||
                v.lang.toLowerCase().includes('hans') ||
                v.lang === 'cmn-Hans-CN'
            ) || voices.find((v) => v.lang.toLowerCase().startsWith('zh'));
          utter.lang = targetVoice?.lang || 'zh-CN';
        } else if (lang === 'zh-TW') {
          targetVoice =
            voices.find(
              (v) =>
                v.lang === 'zh-TW' ||
                v.lang === 'zh-HK' ||
                v.lang.toLowerCase().includes('hant') ||
                v.lang === 'cmn-Hant-TW'
            ) || voices.find((v) => v.lang.toLowerCase().startsWith('zh'));
          utter.lang = targetVoice?.lang || 'zh-TW';
        } else if (lang === 'ceb') {
          targetVoice =
            voices.find((v) => v.lang.toLowerCase().includes('ceb')) ||
            voices.find(
              (v) =>
                v.lang === 'fil-PH' ||
                v.lang.startsWith('fil') ||
                v.lang.startsWith('tl')
            );
          utter.lang = targetVoice?.lang || 'fil-PH';
        } else if (lang === 'tl') {
          targetVoice = voices.find(
            (v) =>
              v.lang === 'fil-PH' ||
              v.lang.startsWith('fil') ||
              v.lang.startsWith('tl')
          );
          utter.lang = targetVoice?.lang || 'fil-PH';
        } else if (lang === 'es') {
          targetVoice = voices.find((v) => v.lang.startsWith('es'));
          utter.lang = targetVoice?.lang || 'es-ES';
        } else {
          // English: Prioritize warm natural female voices (Samantha, Karen, Victoria, Zira, Google UK/US Female, Natural)
          const enVoices = voices.filter((v) => v.lang.toLowerCase().startsWith('en'));
          targetVoice =
            enVoices.find((v) => {
              const n = v.name.toLowerCase();
              return (
                n.includes('google') &&
                (n.includes('female') || n.includes('natural') || n.includes('en-us') || n.includes('en-gb'))
              );
            }) ||
            enVoices.find((v) => {
              const n = v.name.toLowerCase();
              return (
                n.includes('samantha') ||
                n.includes('karen') ||
                n.includes('victoria') ||
                n.includes('zira') ||
                n.includes('fiona') ||
                n.includes('jenny') ||
                n.includes('aria') ||
                n.includes('natural') ||
                n.includes('online') ||
                n.includes('female')
              );
            }) ||
            enVoices[0];
          utter.lang = targetVoice?.lang || 'en-US';
        }

        if (targetVoice) {
          utter.voice = targetVoice;
        }
        window.speechSynthesis.speak(utter);
      } catch {
        // Ignore
      }
    }
  }
}

export const soundEngine = new SoundAndSpeechEngine();

if (typeof window !== 'undefined') {
  (window as any).__QB_SOUND_ENGINE__ = soundEngine;
}
